const toStr = (v) => (v == null ? v : String(v));

export const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

/** Pai "vazio": null, string vazia ou Guid.Empty (raiz no gold do Conta Azul V2/Vehro). */
export function isRootParent(cod) {
  if (cod == null) return true;
  const s = String(cod).trim();
  return s === "" || s === ZERO_GUID;
}

/**
 * Ancestrais de uma categoria por id, do pai direto até a raiz (N níveis).
 * index: Map<key, { parentKey }>. Para em raiz, pai ausente do índice (órfão) ou ciclo.
 */
export function getCategoryAncestors(index, cod) {
  const chain = [];
  if (!index || cod == null) return chain;
  const seen = new Set([cod]);
  let parent = index.get(cod)?.parentKey;
  while (!isRootParent(parent) && index.has(parent) && !seen.has(parent)) {
    chain.push(parent);
    seen.add(parent);
    parent = index.get(parent).parentKey;
  }
  return chain;
}

/**
 * Caminho topo → pai direto de uma linha do fluxo de caixa (`categoriaHierarquia` do backend),
 * normalizado para [{ cod, descricao }]. Vazio quando o backend não manda (ERPs sem o campo) ou
 * quando a categoria já é raiz.
 */
export function getRowCategoryPath(item) {
  const raw = item?.categoriaHierarquia ?? item?.CategoriaHierarquia;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => ({
      cod: p?.cod ?? p?.Cod ?? null,
      descricao: p?.descricao ?? p?.Descricao ?? null,
    }))
    .filter((p) => p.cod != null || p.descricao);
}

/** Constrói árvore para OMIE a partir de lista flat [{value,label}] onde value é "1.01.02" etc. */
export function buildOmieTree(list) {
  const nodesByKey = new Map();
  const roots = [];

  // cria todos os nós
  for (const it of list) {
    const key = toStr(it.value);
    nodesByKey.set(key, { key, value: key, title: it.label, children: [] });
  }

  // liga pais/filhos pela notação pontuada
  for (const it of list) {
    const key = toStr(it.value);
    const parentKey = getOmieParentKey(key);
    const node = nodesByKey.get(key);
    const parent = parentKey ? nodesByKey.get(parentKey) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const flatLabelMap = flattenLabelMap(roots);
  return { tree: roots, flatLabelMap };
}

function getOmieParentKey(code) {
  if (!code || typeof code !== 'string') return null;
  const parts = code.split('.');
  if (parts.length <= 1) return null;
  const parent = parts.slice(0, -1).join('.');
  return parent || null;
}

/** Constrói árvore usando "hierarquia" (id do pai) */
export function buildHierarquiaTree(list) {
  // list: [{ value, label, hierarquia }]
  const nodesByKey = new Map();
  const roots = [];

  for (const it of list) {
    const key = toStr(it.value);
    nodesByKey.set(key, { key, value: key, title: it.label, children: [] });
  }

  for (const it of list) {
    const key = toStr(it.value);
    const parentKey = toStr(it.hierarquia) || '';
    const node = nodesByKey.get(key);
    const parent = parentKey && nodesByKey.get(parentKey);
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const flatLabelMap = flattenLabelMap(roots);
  return { tree: roots, flatLabelMap };
}

/** Mapa value->title */
export function flattenLabelMap(treeNodes) {
  const map = {};
  const stack = [...(treeNodes || [])];
  while (stack.length) {
    const n = stack.pop();
    map[toStr(n.value)] = n.title;
    if (n.children && n.children.length) stack.push(...n.children);
  }
  return map;
}

/** Coleta todas as keys (inclusive descendentes) de uma lista de nós selecionados */
export function expandSelectionToLeaves(treeNodes, selectedKeys) {
  const selected = new Set((selectedKeys || []).map(toStr));
  const allKeys = new Set();

  // index para lookup
  const byKey = {};
  const stack = [...(treeNodes || [])];
  while (stack.length) {
    const n = stack.pop();
    byKey[toStr(n.value)] = n;
    if (n.children && n.children.length) stack.push(...n.children);
  }

  // coleta descendentes
  function collectDescendants(node) {
    allKeys.add(toStr(node.value));
    (node.children || []).forEach(collectDescendants);
  }

  for (const k of selected) {
    const node = byKey[k];
    if (node) collectDescendants(node);
  }
  return Array.from(allKeys);
}

/** Filtra árvore pelo conjunto allowedSet com os modos APENAS/IGNORAR/TODOS */
export const MODE = { TODOS: 0, APENAS: 1, IGNORAR: 2 };

export function filterTreeBySet(treeNodes, allowedSet, mode) {
  if (!Array.isArray(treeNodes)) return [];

  // TODOS → devolve original
  if (mode === MODE.TODOS || !allowedSet) return treeNodes;

  // Clona e filtra recursivamente
  function dfs(node) {
    const v = toStr(node.value);
    const children = (node.children || []).map(dfs).filter(Boolean);
    const inSet = allowedSet.has(v);

    if (mode === MODE.APENAS) {
      // mantém se está no set OU tem filho mantido
      if (inSet || children.length) {
        return { ...node, children };
      }
      return null;
    }

    // IGNORAR
    if (mode === MODE.IGNORAR) {
      // remove se está no set; senão mantém filhos que sobraram
      if (inSet) return null;
      return { ...node, children };
    }

    return { ...node, children };
  }

  return treeNodes.map(dfs).filter(Boolean);
}

/** Extrai todas as keys presentes em uma árvore (útil para validar seleções ao mudar filtros) */
export function collectAllKeys(treeNodes) {
  const out = new Set();
  const stack = [...(treeNodes || [])];
  while (stack.length) {
    const n = stack.pop();
    out.add(toStr(n.value));
    if (n.children && n.children.length) stack.push(...n.children);
  }
  return out;
}

/** 
 * @deprecated
 * Use buildHierarquiaTree instead
 * Constrói árvore para OMIE CONSOLIDADO (type=2) agrupando por empresa */
export function buildOmieConsolidatedTree(list) {
  // Estruturas auxiliares
  const companiesByIntegrationId = new Map();
  const integrationMap = {};
  
  // 1. Parse e agrupa por empresa
  for (const it of list) {
    const value = toStr(it.value);
    const label = it.label || '';
    
    // Extrai categoryCode e integrationId
    const parts = value.split('_');
    if (parts.length < 2) continue;
    
    const categoryCode = parts[0];
    const integrationId = parts[1];
    
    // Extrai nome da empresa (após " - ((")
    const companySeparator = ' - ((';
    const companyIndex = label.indexOf(companySeparator);
    let companyName = companyIndex !== -1
      ? label.substring(companyIndex + companySeparator.length).trim()
      : `Empresa ${integrationId}`;
    
    // Remove os parênteses de fechamento ")))" do final
    if (companyName.endsWith('))')) {
      companyName = companyName.slice(0, -2).trim();
    }
    
    const categoryLabel = companyIndex !== -1
      ? label.substring(0, companyIndex).trim()
      : label;
    
    // Registra empresa
    if (!companiesByIntegrationId.has(integrationId)) {
      companiesByIntegrationId.set(integrationId, {
        name: companyName,
        categories: []
      });
      integrationMap[integrationId] = companyName;
    }
    
    // Adiciona categoria à empresa
    companiesByIntegrationId.get(integrationId).categories.push({
      value: value, // mantém o value original com _integrationId
      categoryCode: categoryCode,
      label: categoryLabel
    });
  }
  
  // 2. Constrói árvore para cada empresa
  const roots = [];
  const flatLabelMap = {};
  
  // Ordena empresas por integrationId para consistência
  const sortedCompanies = Array.from(companiesByIntegrationId.entries())
    .sort((a, b) => a[0].localeCompare(b[0]));
  
  for (const [integrationId, companyData] of sortedCompanies) {
    const companyNodeKey = `company_${integrationId}`;
    const companyNode = {
      key: companyNodeKey,
      value: companyNodeKey,
      title: companyData.name,
      children: []
    };
    
    // Constrói hierarquia de categorias dentro da empresa
    const categoryNodesByCode = new Map();
    
    // Cria nós de categoria
    for (const cat of companyData.categories) {
      categoryNodesByCode.set(cat.categoryCode, {
        key: cat.value,
        value: cat.value,
        title: cat.label,
        children: []
      });
      // Adiciona ao flatLabelMap
      flatLabelMap[cat.value] = cat.label;
    }
    
    // Liga pais/filhos pela notação pontuada do categoryCode
    for (const cat of companyData.categories) {
      const node = categoryNodesByCode.get(cat.categoryCode);
      const parentCode = getOmieParentKey(cat.categoryCode);
      const parent = parentCode ? categoryNodesByCode.get(parentCode) : null;
      
      if (parent) {
        parent.children.push(node);
      } else {
        // Categoria raiz, adiciona direto na empresa
        companyNode.children.push(node);
      }
    }
    
    roots.push(companyNode);
  }
  
  return { tree: roots, flatLabelMap, integrationMap };
}